<template>
    <div>
        <el-table
            ref="a2p-brand-bundle-table"
            v-loading="loadingBundles"
            :data="bundles"
            class="w-full"
            stripe
        >
            <!-- Information Column -->
            <el-table-column
                prop="sid"
                label="Information"
                width="320"
            >
                <template v-slot="scope">
                    <InformationColumn
                        :row="scope.row"
                        :show-a2p-profile-bundle="true"
                        :show-customer-profile="true"
                        name-field="a2p_profile_bundle.friendly_name"
                    />
                </template>
            </el-table-column>

            <!-- Timestamps Column -->
            <el-table-column
                label="Timestamps"
                width="140"
            >
                <template v-slot="scope">
                    <TimestampsColumn :row="scope.row" />
                </template>
            </el-table-column>

            <!-- Type Column -->
            <el-table-column
                label="Type"
                prop="type_name"
                width="100"
            >
                <template v-slot="scope">
                    <TypeColumn :row="scope.row" type-field="type_name" />
                </template>
            </el-table-column>

            <!-- Score Column -->
            <el-table-column
                label="Score"
                prop="score"
                width="95"
            >
                <template v-slot="scope">
                    <CountColumn 
                        :value="scope.row.score ?? 0"
                        :max-value="100"
                        :show-mock="scope.row.mock"
                    />
                </template>
            </el-table-column>

            <!-- A2P Campaigns Column -->
            <el-table-column
                label="A2P Campaigns"
                prop="a2p_use_cases_count"
                width="150"
            >
                <template v-slot="scope">
                    <CountColumn 
                        :value="scope.row.a2p_use_cases_count ?? 0"
                        :max-value="5"
                    />
                </template>
            </el-table-column>

            <!-- Attempts Column -->
            <el-table-column
                label="Attempts"
                prop="attempts"
                width="120"
            >
                <template v-slot="scope">
                    <CountColumn 
                        :value="scope.row.attempts ?? 0"
                        :max-value="3"
                    />
                </template>
            </el-table-column>

            <!-- TCR Status Column -->
            <el-table-column
                label="TCR Status"
                prop="identity_status"
                width="120"
            >
                <template v-slot="scope">
                    <TcrStatusColumn :row="scope.row" />
                </template>
            </el-table-column>

            <!-- TCR Status Column -->
            <el-table-column
                label="TCR ID"
                prop="tcr_id"
                width="120"
            >
                <template v-slot="scope">
                    <span>{{ scope.row.tcr_id }}</span>
                </template>
            </el-table-column>

            <!-- Status Column -->
            <el-table-column
                label="Status"
                prop="status_name"
                width="120"
            >
                <template v-slot="scope">
                    <StatusColumn :row="scope.row" />
                </template>
            </el-table-column>

            <!-- Failure Reason Column -->
            <el-table-column
                label="Failure reason"
                prop="failure_reason"
            >
                <template v-slot="scope">
                    <FailureReasonColumn
                        :row="scope.row"
                        :is-a2p-brand="true"
                    />
                </template>
            </el-table-column>

            <!-- Actions Column -->
            <el-table-column
                label="Action"
                width="150"
            >
                <template v-slot="scope">
                    <div>
                        <el-tooltip
                            placement="top"
                            content="This bundle is already in use."
                            :disabled="!disableUseThisBundleButton(scope.row)"
                        >
                            <el-button
                                type="success"
                                size="mini"
                                class="btn-block"
                                @click="handleUseBundle(scope.row)"
                                :loading="loadingUpdateBundle.includes(scope.row.id)"
                                :disabled="disableUseThisBundleButton(scope.row)"
                            >
                                Use this bundle
                            </el-button>
                        </el-tooltip>
                    </div>
                    <div class="mt-1">
                        <el-tooltip
                            placement="top"
                            content="This bundle cannot be deleted because it belongs to the shared brand."
                            :disabled="!disableDeleteButton(scope.row)"
                        >
                            <el-button
                                type="danger"
                                size="mini"
                                class="btn-block"
                                @click="handleDeleteBundle(scope.row)"
                                :loading="loadingDeleteBundle.includes(scope.row.id)"
                                :disabled="disableDeleteButton(scope.row)"
                            >
                                Delete
                            </el-button>
                        </el-tooltip>
                    </div>
                    <!-- Invoice Button -->
                    <div v-if="shouldShowInvoiceButton(scope.row)" class="mt-1">
                        <button
                            type="button"
                            class="btn btn-xs btn-block purple"
                            @click="openChargebeeInvoice(scope.row)"
                        >
                            <i class="fa fa-link"></i> See invoice
                        </button>
                    </div>
                </template>
            </el-table-column>
        </el-table>
    </div>
</template>

<script>
import { compliance_mixin, acl_mixin } from '../../../mixins'
import InformationColumn from '../columns/information-column'
import TimestampsColumn from '../columns/timestamps-column'
import TypeColumn from '../columns/type-column'
import CountColumn from '../columns/count-column'
import TcrStatusColumn from '../columns/tcr-status-column'
import StatusColumn from '../columns/status-column'
import FailureReasonColumn from '../columns/failure-reason-column'
import { RESTRICTED_TCR_ID } from '../../../constants/compliance-a2p-brand'

export default {
    name: 'A2pBrandBundleTable',
    components: {
        InformationColumn,
        TimestampsColumn,
        TypeColumn,
        CountColumn,
        TcrStatusColumn,
        StatusColumn,
        FailureReasonColumn,
    },
    mixins: [compliance_mixin, acl_mixin],
    props: {
        loadingBundles: {
            type: Boolean,
            required: true,
        },
        bundles: {
            type: Array,
            required: true,
        },
        loadingUpdateBundle: {
            type: Array,
            default: () => [],
        },
        loadingDeleteBundle: {
            type: Array,
            default: () => [],
        }
    },
    data() {
        return {
            deleteTooltip: null,
        }
    },
    methods: {
        disableUseThisBundleButton(compliance_bundle) {
            return compliance_bundle && compliance_bundle.in_use
        },
        disableDeleteButton(compliance_bundle) {
            return compliance_bundle && compliance_bundle.tcr_id === RESTRICTED_TCR_ID
        },
        handleUseBundle(bundle) {
            this.$emit('use-bundle', bundle)
        },
        handleDeleteBundle(bundle) {
            this.$emit('delete-bundle', bundle)
        },
        getChargebeeInvoiceLink(a2p_registration) {
            let invoice_id = a2p_registration.chargebee_registration_invoice_id

            if (!invoice_id) {
                return false
            }

            let env = 'aloware'
            if (['local', 'dev', 'mde', 'testing', 'staging'].includes(localStorage.getItem('app_env'))) {
                env = 'aloware-test'
            }

            return `https://${env}.chargebee.com/admin-console/invoices/${invoice_id}`
        },
        openChargebeeInvoice(a2p_registration) {
            let link = this.getChargebeeInvoiceLink(a2p_registration)
            window.open(link, '_blank')
        },
        shouldShowInvoiceButton(a2p_registration) {
            return this.getChargebeeInvoiceLink(a2p_registration)
        },
    },
}
</script>
